/**
 * The seller's connected stores, loaded once.
 *
 * WHY shared state: the shell's store switcher and every store page need the same list, and the
 * list is also what decides whether "/" shows a picker or jumps straight into the only store.
 * Reloading it per screen would flicker the switcher on every navigation.
 */
import { createContext, use, useMemo, type ReactNode } from "react";
import { api, type Connection } from "./api";
import { useAsync } from "./hooks/useAsync";
import { ErrorState, Loading } from "./components/States";

interface ConnectionsContextValue {
  connections: Connection[];
  reload: () => void;
}

const ConnectionsContext = createContext<ConnectionsContextValue | null>(null);

export function ConnectionsProvider({ children }: { children: ReactNode }): ReactNode {
  const state = useAsync(() => api.connections().then((r) => r.connections), []);
  const value = useMemo<ConnectionsContextValue>(
    () => ({ connections: state.data ?? [], reload: state.reload }),
    [state.data, state.reload],
  );
  if (state.status === "loading") return <div className="page"><Loading label="Loading your stores" /></div>;
  if (state.status === "error") return <div className="page"><ErrorState error={state.error} onRetry={state.reload} /></div>;
  return <ConnectionsContext value={value}>{children}</ConnectionsContext>;
}

export function useConnections(): ConnectionsContextValue {
  const ctx = use(ConnectionsContext);
  if (!ctx) throw new Error("useConnections must be used inside <ConnectionsProvider>");
  return ctx;
}
